import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { DatePicker } from '@/components/ui/date-picker';
import { Listbox } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';

/**
 * Presets plus a custom range, in church-local dates.
 *
 * "This month" is resolved in the church's timezone, not the browser's, so an
 * administrator opening the console from another country sees the church's month.
 * The values are plain `YYYY-MM-DD`; the server converts them to the church's day
 * boundaries.
 */

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'thisYear'
  | 'lastYear'
  | 'allTime'
  | 'custom';

export interface DateRange {
  preset: DateRangePreset;
  from: string | null;
  to: string | null;
}

export interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** Labels for each preset plus the two field names, supplied by the caller. */
  labels: Partial<Record<DateRangePreset, string>> & { from: string; to: string };
  presets?: DateRangePreset[];
  disabled?: boolean;
  /** Forwarded to every inner picker. Required when this sits inside a dialog. */
  popoverPortal?: PopoverPortalTarget;
  className?: string;
}

const SHORT_DATE_RANGE_PRESETS: DateRangePreset[] = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
];

/** Full preset list used when callers do not pass `presets`. */
export const DEFAULT_DATE_RANGE_PRESETS: DateRangePreset[] = [
  ...SHORT_DATE_RANGE_PRESETS,
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'thisYear',
  'allTime',
  'custom',
];

/** Overview and insights panels — no all-time option. */
export const INSIGHTS_DATE_RANGE_PRESETS: DateRangePreset[] = [
  ...SHORT_DATE_RANGE_PRESETS,
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'thisYear',
  'custom',
];

/** List and table filters with an all-time option. */
export const LIST_FILTER_DATE_RANGE_PRESETS: DateRangePreset[] = [
  ...SHORT_DATE_RANGE_PRESETS,
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'thisYear',
  'allTime',
  'custom',
];

/** Filters that include last year but not last month. */
export const EXTENDED_LIST_FILTER_DATE_RANGE_PRESETS: DateRangePreset[] = [
  ...SHORT_DATE_RANGE_PRESETS,
  'thisMonth',
  'thisQuarter',
  'thisYear',
  'lastYear',
  'allTime',
  'custom',
];

/** Dashboard overview cards — month through year plus custom. */
export const DASHBOARD_DATE_RANGE_PRESETS: DateRangePreset[] = [
  ...SHORT_DATE_RANGE_PRESETS,
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'thisYear',
  'custom',
];

const DEFAULT_PRESETS = DEFAULT_DATE_RANGE_PRESETS;

export const DateRangeFilter = ({
  value,
  onChange,
  labels,
  presets = DEFAULT_PRESETS,
  disabled = false,
  popoverPortal = 'body',
  className,
}: DateRangeFilterProps) => {
  const timezone = usePreferences((state) => state.timezone);

  const options = useMemo(
    () =>
      presets.map((preset) => ({
        value: preset,
        label: labels[preset] ?? preset,
      })),
    [labels, presets],
  );

  return (
    <div className={cn('flex flex-wrap items-end gap-2', className)}>
      <Listbox<DateRangePreset>
        value={value.preset}
        onChange={(preset) => {
          if (!preset) return;
          onChange(
            preset === 'custom'
              ? { preset, from: value.from, to: value.to }
              : { preset, ...resolvePreset(preset, timezone) },
          );
        }}
        options={options}
        disabled={disabled}
        className="w-44"
        popoverPortal={popoverPortal}
      />

      {value.preset === 'custom' ? (
        <>
          <div className="w-40">
            <DatePicker
              value={value.from}
              onChange={(from) => onChange({ ...value, from })}
              max={value.to ?? undefined}
              disabled={disabled}
              clearable
              popoverPortal={popoverPortal}
              aria-label={labels.from}
            />
          </div>
          <div className="w-40">
            <DatePicker
              value={value.to}
              onChange={(to) => onChange({ ...value, to })}
              min={value.from ?? undefined}
              disabled={disabled}
              clearable
              popoverPortal={popoverPortal}
              aria-label={labels.to}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

/** Presets resolved against the church's today, never the browser's. */
export const resolvePreset = (
  preset: DateRangePreset,
  timezone: string,
): { from: string | null; to: string | null } => {
  const today = DateTime.now().setZone(timezone);
  const iso = (date: DateTime) => date.toFormat('yyyy-MM-dd');

  switch (preset) {
    case 'today':
      return { from: iso(today), to: iso(today) };
    case 'yesterday': {
      const yesterday = today.minus({ days: 1 });
      return { from: iso(yesterday), to: iso(yesterday) };
    }
    case 'thisWeek':
      return { from: iso(today.startOf('week')), to: iso(today.endOf('week')) };
    case 'lastWeek': {
      const lastWeek = today.minus({ weeks: 1 });
      return { from: iso(lastWeek.startOf('week')), to: iso(lastWeek.endOf('week')) };
    }
    case 'thisMonth':
      return { from: iso(today.startOf('month')), to: iso(today.endOf('month')) };
    case 'lastMonth': {
      const lastMonth = today.minus({ months: 1 });
      return { from: iso(lastMonth.startOf('month')), to: iso(lastMonth.endOf('month')) };
    }
    case 'thisQuarter':
      return { from: iso(today.startOf('quarter')), to: iso(today.endOf('quarter')) };
    case 'thisYear':
      return { from: iso(today.startOf('year')), to: iso(today.endOf('year')) };
    case 'lastYear': {
      const lastYear = today.minus({ years: 1 });
      return { from: iso(lastYear.startOf('year')), to: iso(lastYear.endOf('year')) };
    }
    case 'allTime':
    case 'custom':
    default:
      return { from: null, to: null };
  }
};
